/**
 * Games are addressed by a user-chosen slug: `/play/grandmas-80th`.
 *
 * Slugs are lowercase, digits and hyphens only, which keeps them unambiguous when
 * read aloud, typed on a phone keyboard, or pasted into a chat that auto-lowercases.
 */

export const SLUG_MIN_LENGTH = 3;
export const SLUG_MAX_LENGTH = 32;

/** Strict shape: alphanumeric groups joined by single hyphens, no edge hyphens. */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Slugs we won't hand out. Games live under `/play/`, so none of these actually
 * collide with a route — they're withheld because they'd be confusing to land on,
 * or because we may want the path later.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  'about',
  'admin',
  'api',
  'assets',
  'create',
  'edit',
  'help',
  'index',
  'new',
  'null',
  'play',
  'privacy',
  'random',
  'root',
  'settings',
  'static',
  'support',
  'terms',
  'undefined',
]);

export type SlugProblem = 'too-short' | 'too-long' | 'bad-characters' | 'reserved';

export function checkSlug(slug: string): SlugProblem | null {
  if (slug.length < SLUG_MIN_LENGTH) return 'too-short';
  if (slug.length > SLUG_MAX_LENGTH) return 'too-long';
  if (!SLUG_PATTERN.test(slug)) return 'bad-characters';
  if (RESERVED_SLUGS.has(slug)) return 'reserved';
  return null;
}

export function isValidSlug(slug: string): boolean {
  return checkSlug(slug) === null;
}

export function describeSlugProblem(problem: SlugProblem): string {
  switch (problem) {
    case 'too-short':
      return `At least ${SLUG_MIN_LENGTH} characters.`;
    case 'too-long':
      return `At most ${SLUG_MAX_LENGTH} characters.`;
    case 'bad-characters':
      return 'Letters, numbers and hyphens only.';
    case 'reserved':
      return 'That one is taken by the site itself.';
  }
}

/**
 * Cleans input *while the user is still typing*, which is deliberately more lenient
 * than `isValidSlug`.
 *
 * The difference that matters: a trailing hyphen survives here. Stripping it on every
 * keystroke — as the strict rules would — makes it impossible to type "grandmas-80th",
 * because the hyphen vanishes before you can type what follows it.
 */
export function normalizeSlugInput(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[\s_.]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, SLUG_MAX_LENGTH);
}

/** Applies the strict rules once, on submit. */
export function finalizeSlug(raw: string): string {
  return normalizeSlugInput(raw).replace(/-+$/, '');
}

const ADJECTIVES = [
  'amber', 'balmy', 'blue', 'bold', 'brave', 'bright', 'brisk', 'calm',
  'chirpy', 'clever', 'cosy', 'crisp', 'dandy', 'dapper', 'dawn', 'deft',
  'eager', 'early', 'fancy', 'fleet', 'fond', 'fresh', 'gentle', 'glad',
  'golden', 'grand', 'happy', 'hazel', 'jolly', 'keen', 'kind', 'lively',
  'lucky', 'lunar', 'merry', 'mellow', 'mighty', 'misty', 'neat', 'nifty',
  'noble', 'plucky', 'polite', 'proud', 'quiet', 'rapid', 'rosy', 'royal',
  'sandy', 'silver', 'skyward', 'snug', 'spry', 'sunny', 'swift', 'tidy',
  'tiny', 'vivid', 'warm', 'zesty',
] as const;

const NOUNS = [
  'acorn', 'anchor', 'aspen', 'badger', 'beacon', 'bramble', 'breeze', 'brook',
  'cactus', 'canyon', 'cedar', 'clover', 'comet', 'coral', 'cove', 'dahlia',
  'dune', 'ember', 'falcon', 'fern', 'finch', 'garnet', 'grove', 'harbor',
  'heron', 'island', 'juniper', 'kestrel', 'lagoon', 'lantern', 'lemon', 'lily',
  'lotus', 'maple', 'marble', 'meadow', 'mango', 'nectar', 'oasis', 'orchid',
  'otter', 'panda', 'pebble', 'peony', 'plum', 'prairie', 'quail', 'quartz',
  'raven', 'ridge', 'river', 'robin', 'sable', 'sparrow', 'summit', 'thistle',
  'tulip', 'walnut', 'willow', 'zephyr',
] as const;

function pick<T>(items: readonly T[]): T {
  const buf = new Uint32Array(1);
  globalThis.crypto.getRandomValues(buf);
  return items[buf[0]! % items.length]!;
}

/**
 * A readable default like "sunny-otter", so the field arrives already shareable and
 * the user only has to type if they want something meaningful.
 *
 * 60 x 60 = 3,600 pairs, which collide often — that's fine, because the create flow
 * checks availability and nudges the user before saving.
 */
export function randomSlug(): string {
  return `${pick(ADJECTIVES)}-${pick(NOUNS)}`;
}

/** Adds a short numeric suffix, for offering an alternative when a slug is taken. */
export function suffixSlug(slug: string): string {
  const buf = new Uint32Array(1);
  globalThis.crypto.getRandomValues(buf);
  const n = (buf[0]! % 89) + 10; // 10-98, always two digits
  return `${finalizeSlug(slug).slice(0, SLUG_MAX_LENGTH - 3)}-${n}`;
}
